import { ES_SPECIAL_CHARS } from 'lib/esSpecialChars'
import { phoneDigits } from 'lib/phone'

/** Split a typed query into the words we match on, dropping surrounding whitespace. */
const queryWords = (query: string): string[] => query.trim().split(/\s+/).filter(Boolean)

/**
 * Shortest digit run treated as a phone number. Below this the leading-wildcard scan
 * costs more than it returns ("55" matches most of the index), and three digits are as
 * likely to be part of a name as a number.
 */
export const MIN_PHONE_DIGITS = 4

/** Every phone field the contact index carries as a plain string. */
export const PHONE_FIELDS = [`cellPhone`, `homePhone`, `workPhone`] as const

/** A letterless query with enough digits is a phone number, formatting and all. */
const asPhoneQuery = (query: string): string | undefined => {
  if (/[a-z]/i.test(query)) return undefined
  const digits = phoneDigits(query)
  return digits.length >= MIN_PHONE_DIGITS ? digits : undefined
}

/**
 * ES terms for a free-text contact search box, to be ANDed with whatever else the caller
 * is querying on.
 *
 * Each word becomes its own `name:*word*` term, so partial words and out-of-order words
 * both match: `aug pra` and `pratt aug` each find "Augustine Pratt". A word with an `@`
 * matches email instead, one term per side of the `@`: the index tokenizes email on it,
 * so a wildcard spanning the `@` (`*dany@casco*`) matches nothing. A letterless query of
 * four or more digits is a phone number and matches any phone field, so the last four
 * digits of a caller's number find them. The API analyzes wildcards, so matching is
 * case-insensitive. An empty query yields no terms.
 */
export function contactNameTerms(query: string): string[] {
  const phone = asPhoneQuery(query)
  if (phone) return [`(${PHONE_FIELDS.map((field) => `${field}:*${phone}*`).join(` OR `)})`]
  return queryWords(query).flatMap((word) =>
    word.includes(`@`)
      ? // One term per side of the @ that was actually typed: `@gmail` is just `gmail`.
        word
          .split(`@`)
          .filter(Boolean)
          .map((part) => `email:*${part.replace(ES_SPECIAL_CHARS, `\\$&`)}*`)
      : [`name:*${word.replace(ES_SPECIAL_CHARS, `\\$&`)}*`],
  )
}

type SearchableContact = {
  name?: string | null
  email?: string | null
  cellPhone?: string | null
  homePhone?: string | null
  workPhone?: string | null
}

/**
 * Client-side equivalent of `contactNameTerms`, for contacts that ES has not indexed yet.
 * Keeps locally-added records consistent with the active search instead of letting them
 * show through a filter they don't match.
 */
export function contactMatchesQuery(contact: SearchableContact, query: string): boolean {
  const words = queryWords(query)
  if (words.length === 0) return true
  const phone = asPhoneQuery(query)
  if (phone) return PHONE_FIELDS.some((field) => phoneDigits(contact[field] ?? ``).includes(phone))
  const name = (contact.name ?? ``).toLowerCase()
  const email = (contact.email ?? ``).toLowerCase()
  return words.every((word) =>
    word.includes(`@`)
      ? word
          .toLowerCase()
          .split(`@`)
          .filter(Boolean)
          .every((part) => email.includes(part))
      : name.includes(word.toLowerCase()),
  )
}
